/**
 * The Company Details form, as the server describes it.
 *
 * The shape is deliberately server-driven: the API says which fields exist,
 * which are locked and why, and what the dropdown vocabularies are. Adding a
 * field to the screen is then a backend change plus a line in the form layout,
 * not a new type, a new mapper and a new validator.
 */

/** One choice in a select. */
export interface CompanySettingsOption {
  value: string;
  label: string;
}

/** Every field's current value, keyed by field name. Never null — "" is empty. */
export type CompanySettingsValues = Record<string, string>;

/** Field name to the reason it cannot be edited here. */
export type CompanySettingsLocks = Record<string, string>;

/** Option-list name to its choices. */
export type CompanySettingsOptions = Record<string, CompanySettingsOption[]>;

/**
 * ZOUR-19 — Zoho Books' own answer about this org's GST registration.
 *
 * Shown as its own read-only block on the GST panel, because the step that
 * turns GST on happens in Zoho Books and Zournal can only report on it.
 */
export interface GstZohoRegistration {
  reachable: boolean;
  registered: boolean | null;
  gstin: string | null;
  registeredOn: string | null;
  organizationId: string | null;
  error: string | null;
}

/** ZOUR-19 — documents still waiting to be reviewed or pushed. */
export interface GstBacklog {
  invoices: number | null;
  bills: number | null;
  total: number | null;
}

export interface CompanySettings {
  companyId: number;
  values: CompanySettingsValues;
  locked: CompanySettingsLocks;
  options: CompanySettingsOptions;
  /** When the business context was last saved, or null if never. */
  updatedAt: string | null;
  /**
   * Whether a write-only credential is already stored.
   *
   * Only meaningful on panels that have one — the GST panel's Cashfree secret.
   * Its value never leaves the server, so this flag is all the form learns, and
   * it is what turns the field's footnote from "Not configured" into
   * "Configured". Undefined on panels with nothing write-only.
   */
  secretSet?: boolean;
  /**
   * ZOUR-19, GST panel only. Undefined means the server did not look this time
   * — which is NOT the same as `reachable: false`.
   */
  zoho?: GstZohoRegistration;
  /** ZOUR-19, GST panel only. */
  backlog?: GstBacklog;
  /**
   * ZOUR-19 — warnings about a save that COMMITTED.
   *
   * A date that disagrees with Zoho's, or Zoho being unreachable when we wanted
   * to cross-check. Never a failure: Zournal's column is what Zournal enforces,
   * so the save stands and the user is told.
   */
  warnings?: string[];
}

/** ZOUR-19 — one document the "past documents" check listed. */
export interface GstFlaggedDocument {
  kind: "invoice" | "bill";
  id: number;
  documentNumber: string;
  date: string | null;
}

/** ZOUR-19 — one contact the "customers and vendors" check listed. */
export interface GstFlaggedContact {
  kind: "customer" | "vendor";
  id: number;
  name: string;
}

/**
 * ZOUR-19 — the result of the two on-demand checks.
 *
 * REPORT ONLY. Neither check fixes anything: past documents are historical
 * records, and a contact's GST treatment is a judgement about who that party
 * is. Both lists exist so a professional can go and look.
 */
/** ZOUR-19 — a contact classified in Zournal but blank in Zoho. */
export interface GstZohoContactMismatch {
  kind: "customer" | "vendor";
  id: number;
  name: string;
  localTreatment: string;
}

export interface GstChecks {
  pastDocuments: {
    checked: boolean;
    gstEnabledFrom: string | null;
    documents: GstFlaggedDocument[];
    truncated: boolean;
    message: string;
  };
  contacts: {
    contacts: GstFlaggedContact[];
    truncated: boolean;
    message: string;
  };
  /**
   * ZOUR-19 review 14 — contacts whose GST treatment is set here but blank in
   * Zoho, which is what happens to anything created before the switch. Zoho
   * rejects documents against them, and the local-column check cannot see it.
   */
  zohoContacts?: {
    checked: boolean;
    contacts: GstZohoContactMismatch[];
    truncated: boolean;
    message: string;
  };
}

/** True when the server marked this field read-only. */
export const isFieldLocked = (settings: CompanySettings, field: string): boolean =>
  Boolean(settings.locked[field]);

/** The reason a field is read-only, or null when it is editable. */
export const lockReason = (settings: CompanySettings, field: string): string | null =>
  settings.locked[field] ?? null;
